import { fabric } from "fabric";
import model3dStageBackgroundUrl from "@/assets/images/board/model3d-stage-bg.webp";

let stageBackgroundImage: HTMLImageElement | null = null;
let stageBackgroundLoadFailed = false;

/** 「查看模型」按钮使用的 iconfont symbol id */
const MODEL3D_ICON_SYMBOL_ID = "icon-wensheng3D";

let model3dIconImage: HTMLImageElement | null = null;
type Model3dIconLoadState = "idle" | "loading" | "ready" | "failed";
let model3dIconLoadState: Model3dIconLoadState = "idle";

const MODEL3D_OVERLAY_STYLE = {
  tagWidth: 86,
  tagHeight: 24,
  tagRadius: 16,
  tagRight: 8,
  tagTop: 8,
  tagIconSize: 14,
  tagIconLeft: 8,
  tagIconTop: 5,
  tagTextLeft: 28,
  tagTextCenterY: 12,
  tagFontSize: 12,
  hitTolerance: 12,
};

const GLASS_FILL = "rgba(60, 60, 67, 0.6)";
const GLASS_STROKE = "rgba(235, 235, 245, 0.15)";

const getStageBackgroundImage = (canvas?: fabric.Canvas | null) => {
  if (stageBackgroundLoadFailed || typeof Image === "undefined") return null;

  if (!stageBackgroundImage) {
    stageBackgroundImage = new Image();
    stageBackgroundImage.onload = () => canvas?.requestRenderAll();
    stageBackgroundImage.onerror = () => {
      stageBackgroundLoadFailed = true;
    };
    stageBackgroundImage.src = model3dStageBackgroundUrl;
  }

  return stageBackgroundImage;
};

const drawStageBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  canvas?: fabric.Canvas | null,
) => {
  if (!width || !height) return;

  const left = -width / 2;
  const top = -height / 2;
  const image = getStageBackgroundImage(canvas);

  ctx.save();
  ctx.fillStyle = "#111213";
  ctx.fillRect(left, top, width, height);

  if (image?.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    const targetRatio = width / height;
    let sx = 0;
    let sy = 0;
    let sw = image.naturalWidth;
    let sh = image.naturalHeight;

    if (sourceRatio > targetRatio) {
      sw = image.naturalHeight * targetRatio;
      sx = (image.naturalWidth - sw) / 2;
    } else {
      sh = image.naturalWidth / targetRatio;
      sy = (image.naturalHeight - sh) / 2;
    }

    ctx.drawImage(image, sx, sy, sw, sh, left, top, width, height);
  }

  ctx.restore();
};

const getCanvasZoom = (obj: fabric.Object): number => {
  const zoom = (obj.canvas as any)?.getZoom?.();
  return typeof zoom === "number" && Number.isFinite(zoom) && zoom > 0
    ? zoom
    : 1;
};

const getObjectWorldScale = (obj: fabric.Object): number => {
  const calcTransformMatrix = (obj as any).calcTransformMatrix;
  if (typeof calcTransformMatrix === "function") {
    const matrix = calcTransformMatrix.call(obj);
    if (Array.isArray(matrix)) {
      const { scaleX, scaleY } = fabric.util.qrDecompose(matrix);
      return Math.max(Math.abs(scaleX), Math.abs(scaleY), 1e-6);
    }
  }

  return Math.max(Math.abs(obj.scaleX ?? 1), Math.abs(obj.scaleY ?? 1), 1e-6);
};

const getOverlayUnitScale = (obj: fabric.Object): number => {
  return 1 / Math.max(getObjectWorldScale(obj) * getCanvasZoom(obj), 1e-6);
};

const getModel3dOverlayLayout = (obj: fabric.Object) => {
  // _render / 命中检测均在对象本地坐标系中，勿再乘 scaleX/scaleY（Fabric 会在绘制时统一缩放）
  const width = obj.width || 0;
  const height = obj.height || 0;
  const unit = getOverlayUnitScale(obj);
  const style = MODEL3D_OVERLAY_STYLE;

  const tag = {
    left: width / 2 - (style.tagRight + style.tagWidth) * unit,
    top: -height / 2 + style.tagTop * unit,
    width: style.tagWidth * unit,
    height: style.tagHeight * unit,
    radius: style.tagRadius * unit,
  };
  return { unit, tag };
};

/** 封面图拉伸至与舞台背景相同的本地显示区域 */
const drawCoverImage = (
  ctx: CanvasRenderingContext2D,
  element: HTMLImageElement | undefined,
  width: number,
  height: number,
) => {
  if (!element?.complete || !width || !height) return;
  if (element.naturalWidth <= 0 || element.naturalHeight <= 0) return;

  ctx.save();
  ctx.drawImage(element, -width / 2, -height / 2, width, height);
  ctx.restore();
};

/**
 * 从 iconfont symbol 缓存「查看模型」图标为 Image。
 * iconfont.js 为 async 加载；symbol 未就绪时保持 idle，下次 _render 再试。
 */
const getModel3dIconImage = (canvas?: fabric.Canvas | null) => {
  if (model3dIconLoadState === "failed" || typeof Image === "undefined") return null;
  if (model3dIconLoadState === "ready") return model3dIconImage;
  if (model3dIconLoadState === "loading") return model3dIconImage;
  if (typeof document === "undefined") return null;

  const symbol = document.getElementById(MODEL3D_ICON_SYMBOL_ID);
  if (!symbol) return null;

  model3dIconLoadState = "loading";
  const viewBox = symbol.getAttribute("viewBox") || "0 0 1024 1024";
  // symbol 内 path 无 fill，依赖外层 fill 上色（按钮文字同为白色）
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="64" height="64" fill="#FFFFFF">${symbol.innerHTML}</svg>`;
  const img = new Image();
  img.onload = () => {
    model3dIconImage = img;
    model3dIconLoadState = "ready";
    canvas?.requestRenderAll();
  };
  img.onerror = () => {
    model3dIconLoadState = "failed";
  };
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return null;
};

const drawModel3dIcon = (
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  size: number,
  canvas?: fabric.Canvas | null,
) => {
  const image = getModel3dIconImage(canvas);
  if (image?.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
    ctx.drawImage(image, left, top, size, size);
  }
};

class FabricModel3d extends fabric.Image {
  public type = "model3d";

  public toObject(propertiesToInclude?: string[]) {
    const base = super.toObject(propertiesToInclude);
    return {
      ...base,
      type: "model3d",
      extraData: (this as any).extraData,
    };
  }

  static fromObject(object: any, callback: (obj: fabric.Object) => void) {
    const src = object?.src || "";
    const crossOrigin = object?.crossOrigin || "anonymous";
    (fabric.util as any).loadImage(
      src,
      (img: HTMLImageElement | null) => {
        const element = img || new Image();
        (element as any).crossOrigin = crossOrigin;
        const instance = new FabricModel3d(element, {
          ...object,
          crossOrigin,
        });
        (instance as any).type = "model3d";
        (instance as any).extraData = object?.extraData;
        callback(instance);
      },
      undefined,
      crossOrigin,
    );
  }

  _render(ctx: CanvasRenderingContext2D) {
    // 本地坐标系尺寸（width/height）；Fabric 会在 _render 外统一应用 scaleX/scaleY
    const width = this.width || 0;
    const height = this.height || 0;
    drawStageBackground(ctx, width, height, this.canvas);
    drawCoverImage(ctx, this._element as HTMLImageElement | undefined, width, height);

    const isHovered = (this as any).__hovered === true;
    const isSelected = this.canvas ? this.canvas.getActiveObject() === this : false;
    if (!isHovered && !isSelected) return;

    const { unit, tag } = getModel3dOverlayLayout(this);
    const style = MODEL3D_OVERLAY_STYLE;

    ctx.save();

    this._drawRoundRect(ctx, tag.left, tag.top, tag.width, tag.height, tag.radius);
    ctx.fillStyle = GLASS_FILL;
    ctx.fill();
    ctx.strokeStyle = GLASS_STROKE;
    ctx.lineWidth = unit;
    ctx.stroke();

    drawModel3dIcon(
      ctx,
      tag.left + style.tagIconLeft * unit,
      tag.top + style.tagIconTop * unit,
      style.tagIconSize * unit,
      this.canvas,
    );

    ctx.fillStyle = "#FFFFFF";
    ctx.font = `400 ${style.tagFontSize * unit}px "PingFang SC", sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(
      "查看模型",
      tag.left + style.tagTextLeft * unit,
      tag.top + style.tagTextCenterY * unit,
    );

    ctx.restore();
  }

  private _drawRoundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) {
    const r = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.arc(x + width - r, y + r, r, -Math.PI / 2, 0);
    ctx.lineTo(x + width, y + height - r);
    ctx.arc(x + width - r, y + height - r, r, 0, Math.PI / 2);
    ctx.lineTo(x + r, y + height);
    ctx.arc(x + r, y + height - r, r, Math.PI / 2, Math.PI);
    ctx.lineTo(x, y + r);
    ctx.arc(x + r, y + r, r, Math.PI, (3 * Math.PI) / 2);
    ctx.closePath();
  }
}

let _fabricModel3dRegistered = false;

export const ensureFabricModel3dRegistered = () => {
  if (_fabricModel3dRegistered) return;
  if ((fabric as any).Model3d?.prototype?.type === "model3d") {
    _fabricModel3dRegistered = true;
    return;
  }
  (fabric as any).Model3d = FabricModel3d;
  _fabricModel3dRegistered = true;
};

export const isModel3dLayer = (
  obj: fabric.Object | undefined | null,
): boolean => {
  return (obj as any)?.type === "model3d";
};

export function getViewModelButtonHitBounds(
  obj: fabric.Object,
): { left: number; top: number; width: number; height: number } | null {
  if (!isModel3dLayer(obj)) return null;

  const { unit, tag } = getModel3dOverlayLayout(obj);
  const tolerance = MODEL3D_OVERLAY_STYLE.hitTolerance * unit;
  const left = tag.left - tolerance;
  const top = tag.top - tolerance;
  const right = tag.left + tag.width + tolerance;
  const bottom = tag.top + tag.height + tolerance;

  return {
    left,
    top,
    width: right - left,
    height: bottom - top,
  };
}

export function isClickOnViewModelButton(
  obj: fabric.Object,
  localPoint: { x: number; y: number },
): boolean {
  const bounds = getViewModelButtonHitBounds(obj);
  if (!bounds) return false;

  return (
    localPoint.x >= bounds.left &&
    localPoint.x <= bounds.left + bounds.width &&
    localPoint.y >= bounds.top &&
    localPoint.y <= bounds.top + bounds.height
  );
}
